import { useCallback, useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

const INITIAL_GRADIENT_ANGLE = 48;
const FULL_ROTATION_DEGREES = 360;
const GRADIENT_ROTATION_INCREMENT_DEGREES = 1;
const GRADIENT_ROTATION_INTERVAL_MS = 16;
const FULL_PROGRESS = 1;
const INITIAL_PROGRESS = 0;
const DIMMED_LOADING_OPACITY = 0.5;
const DEFAULT_LOADING_OPACITY = 1;
const LOADING_REVEAL_DURATION_MS = 400;
const LOADING_FILL_DURATION_MS = 300;
const LOADING_FADE_DURATION_MS = 400;
const LOADING_FADE_DELAY_MS = 50;
const LOADING_END_STOP_OPACITY = 0.62;

enum LoadingTransition {
  None = 'none',
  Opening = 'opening',
  Closing = 'closing',
}

interface UseContainerLoadingBorderInput {
  loading: boolean;
}

export interface ContainerLoadingBorderState {
  isLoadingActive: boolean;
  isClosing: boolean;
  loadingOpacity: number;
  gradientAngle: number;
  borderRevealProgress: number;
  endStopOpacity: number;
}

interface LoadingBorderStateSetters {
  setIsLoadingActive: Dispatch<SetStateAction<boolean>>;
  setIsClosing: Dispatch<SetStateAction<boolean>>;
  setLoadingOpacity: Dispatch<SetStateAction<number>>;
  setBorderRevealProgress: Dispatch<SetStateAction<number>>;
  setEndStopOpacity: Dispatch<SetStateAction<number>>;
}

export function useContainerLoadingBorder(input: UseContainerLoadingBorderInput): ContainerLoadingBorderState {
  const { loading } = input;
  const [isLoadingActive, setIsLoadingActive] = useState(loading);
  const [isClosing, setIsClosing] = useState(false);
  const [loadingOpacity, setLoadingOpacity] = useState(loading ? DIMMED_LOADING_OPACITY : DEFAULT_LOADING_OPACITY);
  const [gradientAngle, setGradientAngle] = useState(INITIAL_GRADIENT_ANGLE);
  const [borderRevealProgress, setBorderRevealProgress] = useState(loading ? FULL_PROGRESS : INITIAL_PROGRESS);
  const [endStopOpacity, setEndStopOpacity] = useState(INITIAL_PROGRESS);
  const animationSetters = {
    setIsLoadingActive,
    setIsClosing,
    setLoadingOpacity,
    setBorderRevealProgress,
    setEndStopOpacity,
  };
  const startOpeningAnimation = useLoadingOpeningAnimation(animationSetters);
  const startClosingAnimation = useLoadingClosingAnimation(animationSetters);

  useEffect(() => {
    syncLoadingTransition({
      loading,
      isLoadingActive,
      startOpeningAnimation,
      startClosingAnimation,
    });
  }, [loading, isLoadingActive, startClosingAnimation, startOpeningAnimation]);

  useEffect(() => {
    if (!isLoadingActive || isClosing) {
      return;
    }

    const interval = setInterval(() => {
      setGradientAngle((previousAngle) =>
        (previousAngle + GRADIENT_ROTATION_INCREMENT_DEGREES) % FULL_ROTATION_DEGREES,
      );
    }, GRADIENT_ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isLoadingActive, isClosing]);

  return {
    isLoadingActive,
    isClosing,
    loadingOpacity,
    gradientAngle,
    borderRevealProgress,
    endStopOpacity,
  };
}

function useLoadingOpeningAnimation(setters: LoadingBorderStateSetters): () => void {
  const {
    setIsLoadingActive,
    setIsClosing,
    setBorderRevealProgress,
    setEndStopOpacity,
    setLoadingOpacity,
  } = setters;

  return useCallback((): void => {
    requestAnimationFrame(() => {
      setIsLoadingActive(true);
      setIsClosing(false);
      setBorderRevealProgress(INITIAL_PROGRESS);
      setEndStopOpacity(INITIAL_PROGRESS);

      const startTime = Date.now();
      const animateReveal = (): void => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / LOADING_REVEAL_DURATION_MS, FULL_PROGRESS);
        setBorderRevealProgress(progress);
        setLoadingOpacity(DEFAULT_LOADING_OPACITY - progress * DIMMED_LOADING_OPACITY);

        if (progress < FULL_PROGRESS) {
          requestAnimationFrame(animateReveal);
        }
      };

      requestAnimationFrame(animateReveal);
    });
  }, [setBorderRevealProgress, setEndStopOpacity, setIsClosing, setIsLoadingActive, setLoadingOpacity]);
}

function useLoadingClosingAnimation(setters: LoadingBorderStateSetters): () => void {
  const {
    setIsLoadingActive,
    setIsClosing,
    setBorderRevealProgress,
    setEndStopOpacity,
    setLoadingOpacity,
  } = setters;

  return useCallback((): void => {
    requestAnimationFrame(() => {
      setIsClosing(true);

      const startTime = Date.now();
      const animateFill = (): void => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / LOADING_FILL_DURATION_MS, FULL_PROGRESS);
        setEndStopOpacity(progress * LOADING_END_STOP_OPACITY);

        if (progress < FULL_PROGRESS) {
          requestAnimationFrame(animateFill);
          return;
        }

        scheduleLoadingFadeOut({
          setIsLoadingActive,
          setIsClosing,
          setBorderRevealProgress,
          setEndStopOpacity,
          setLoadingOpacity,
        });
      };

      requestAnimationFrame(animateFill);
    });
  }, [setBorderRevealProgress, setEndStopOpacity, setIsClosing, setIsLoadingActive, setLoadingOpacity]);
}

function scheduleLoadingFadeOut(setters: LoadingBorderStateSetters): void {
  setTimeout(() => {
    const fadeStartTime = Date.now();
    const animateFadeOut = (): void => {
      const fadeElapsed = Date.now() - fadeStartTime;
      const fadeProgress = Math.min(fadeElapsed / LOADING_FADE_DURATION_MS, FULL_PROGRESS);
      setters.setBorderRevealProgress(FULL_PROGRESS - fadeProgress);
      setters.setEndStopOpacity(LOADING_END_STOP_OPACITY * (FULL_PROGRESS - fadeProgress));
      setters.setLoadingOpacity(DIMMED_LOADING_OPACITY + fadeProgress * DIMMED_LOADING_OPACITY);

      if (fadeProgress < FULL_PROGRESS) {
        requestAnimationFrame(animateFadeOut);
        return;
      }

      setters.setIsLoadingActive(false);
      setters.setIsClosing(false);
      setters.setEndStopOpacity(INITIAL_PROGRESS);
    };

    requestAnimationFrame(animateFadeOut);
  }, LOADING_FADE_DELAY_MS);
}

function syncLoadingTransition(input: {
  loading: boolean;
  isLoadingActive: boolean;
  startOpeningAnimation: () => void;
  startClosingAnimation: () => void;
}): void {
  const transitionHandlers: Record<LoadingTransition, () => void> = {
    [LoadingTransition.None]: noop,
    [LoadingTransition.Opening]: input.startOpeningAnimation,
    [LoadingTransition.Closing]: input.startClosingAnimation,
  };

  transitionHandlers[resolveLoadingTransition(input)]();
}

function resolveLoadingTransition(input: {
  loading: boolean;
  isLoadingActive: boolean;
}): LoadingTransition {
  if (input.loading === input.isLoadingActive) {
    return LoadingTransition.None;
  }

  return input.loading ? LoadingTransition.Opening : LoadingTransition.Closing;
}

function noop(): void {
  return undefined;
}
